import axios, { AxiosError, AxiosInstance, AxiosRequestConfig, AxiosResponse, InternalAxiosRequestConfig } from 'axios';
import createAuthRefreshInterceptor from 'axios-auth-refresh';
import qs from 'qs';
import { createContext, PropsWithChildren, useCallback, useContext, useMemo, useState } from 'react';
import store from 'store2';
import { APIError, DashboardUserLoginInfo, LoginToken } from '../API/APIObjects';
import { SessionStorageKeys } from '../CommonUtils/Storage';
import { useLocalization } from './Localization';

export const LoginTokenTypes = {
    GoMoto: 'GoMoto',
    DlrSecured: 'dlrSecured',
} as const;

type AxiosStateType = {
    instance: AxiosInstance | undefined;
    dSIMInstance: AxiosInstance | undefined;
    authInstance: AxiosInstance | undefined;
    sessionValid: boolean;
    resetSession: () => void;
};

const initialState: AxiosStateType = {
    instance: undefined,
    dSIMInstance: undefined,
    authInstance: undefined,
    sessionValid: false,
    resetSession: () => null,
};

let abortController = new AbortController();

const AxiosStateContext = createContext<AxiosStateType>(initialState);

export const AxiosProvider: React.FC<PropsWithChildren> = (props) => {
    const [sessionValid, setSessionValid] = useState<boolean>(true);

    const axiosSuccessResponseInterceptor = useCallback((response: AxiosResponse) => response, []);

    const axiosErrorResponseInterceptor = useCallback((error: AxiosError) => {
        const sentryIgnoreErrors: number[] = [401];
        const sentryIgnoreResponseErrors: string[] = ['login_required'];

        if (!sentryIgnoreErrors.includes(error.request?.status) &&
            !sentryIgnoreResponseErrors.includes((error.response?.data as any)?.error)) {
            if (error.response && error.response.data) {
                console.error(
                    '-- Axios Error (API Error) -- \n' +
                    (error.response.config && error.response.config.url ? error.response.config.url : '') +
                    '\n' +
                    JSON.stringify(error.response.data),
                );
            }
            else if (error.code === 'ERR_CANCELED') {
                console.log(
                    '-- Axios Error (Canceled) -- \n' +
                    (error.config && error.config.url ? error.config.url : ''),
                );
            }
            else {
                console.error(
                    '-- Axios Error (Network Error) -- \n' +
                    (error.config && error.config.url ? error.config.url : '') +
                    '\n' +
                    JSON.stringify(error),
                );
            }
        }

        return Promise.reject(error);
    }, []);

    /**
     * Axios instance for use with dlrSecured calls
     */
    const dSIMInstance = useMemo(() => {
        const a = axios.create({
            baseURL: process.env.REACT_APP_DSIM_BASE_URL,
            headers: {
                'content-type': 'application/x-www-form-urlencoded',
            },
        });

        a.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
            request.signal = abortController.signal;

            return request;
        }, (error: AxiosError) => {
            return Promise.reject(error);
        });

        a.interceptors.response.use(axiosSuccessResponseInterceptor, axiosErrorResponseInterceptor);

        return a;
    }, [axiosSuccessResponseInterceptor, axiosErrorResponseInterceptor]);

    /**
     * Axios instance for use to obtain JWT from GoMotoCore
     */
    const authInstance = useMemo(() => {
        const a = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
            headers: {
                'accept': 'application/json',
                'Content-Type': 'application/json',
            },
        });

        a.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
            let token = store.session.get(SessionStorageKeys.Token);

            request.headers.set('Authorization', `Bearer ${token}`);
            request.signal = abortController.signal;

            return request;
        }, (error: AxiosError) => {
            return Promise.reject(error);
        });

        return a;

    }, []);

    const Authorize = useCallback(async () => {
        try {
            // Check to make sure the user still exists. HTTP 401 will be thrown if not
            if (store.session.has(SessionStorageKeys.Username)) {
                await authInstance.put<DashboardUserLoginInfo>('Login/Info', JSON.stringify(store.session.get(SessionStorageKeys.Username)));
            }

            if (store.session.has(SessionStorageKeys.RefreshToken)) {
                switch (store.session.get(SessionStorageKeys.TokenType)) {
                    // dlrSecured generated JWT
                    case LoginTokenTypes.DlrSecured:
                        let dSIMResponse = (await dSIMInstance.post('Token',
                            'grant_type=refresh_token&' +
                            `refresh_token=${store.session.get(SessionStorageKeys.RefreshToken)}&` +
                            'scope=openid%20email',
                        )).data;

                        if (dSIMResponse && dSIMResponse['access_token']) {
                            let token = JSON.parse(atob(dSIMResponse['access_token']))['token'];
                            store.session.set(SessionStorageKeys.Token, token);
                            store.session.set(SessionStorageKeys.RefreshToken, dSIMResponse['refresh_token']);

                            return token;
                        }
                        else {
                            throw new Error('No Token');
                        }
                    // GoMotoCore generated JWT
                    case LoginTokenTypes.GoMoto:
                        let gmResponse = (await authInstance.post<LoginToken>('/Login/Refresh', JSON.stringify(store.session.get(SessionStorageKeys.RefreshToken)))).data;

                        if (gmResponse.token) {
                            store.session.set(SessionStorageKeys.Token, gmResponse.token);
                            store.session.set(SessionStorageKeys.RefreshToken, gmResponse.refresh_token);

                            return gmResponse.token;
                        }
                        else {
                            throw new Error('No Token');
                        }
                    default:
                        throw new Error('Invalid Token Type');
                }
            }
            else {
                throw new Error('No Refresh Token');
            }
        }
        catch {
            setSessionValid(false);
        }

        return '';
    }, [dSIMInstance, authInstance]);

    /**
     * Axios instance for use with API calls
     */
    const instance = useMemo(() => {
        const a = axios.create({
            baseURL: process.env.REACT_APP_API_BASE_URL,
            headers: {
                'Accept': 'application/json',
                'content-type': 'application/json',
            },
            paramsSerializer: (params) => qs.stringify(params),
        });

        a.interceptors.request.use(async (request: InternalAxiosRequestConfig) => {
            let token = store.session.get(SessionStorageKeys.Token);

            if (!token) {
                token = (await Authorize()).token;
            }

            request.headers.set('Authorization', `Bearer ${token}`);
            request.signal = abortController.signal;

            return request;
        }, (error: AxiosError) => {
            return Promise.reject(error);
        });
        a.interceptors.response.use(axiosSuccessResponseInterceptor, axiosErrorResponseInterceptor);

        createAuthRefreshInterceptor(a, async (failedRequest: any) => {
            const token: string = await Authorize();
            if (!token) {
                return Promise.reject(new AxiosError(undefined, 'SESSION_EXPIRED'));
            }

            failedRequest.response.config.headers['Authorization'] = `Bearer ${token}`;
            return Promise.resolve();
        });

        return a;
    }, [axiosSuccessResponseInterceptor, axiosErrorResponseInterceptor, Authorize]);

    return (
        <AxiosStateContext.Provider
            value={{
                instance: instance,
                dSIMInstance: dSIMInstance,
                authInstance: authInstance,
                sessionValid: sessionValid,
                resetSession: useCallback(() => setSessionValid(true), []),
            }}
        >
            {props.children}
        </AxiosStateContext.Provider>
    );
};

/**
 * Axios instance for use with API calls
 */
const useAxiosInstance = () => {
    const context = useContext(AxiosStateContext);
    if (!context || !context.instance) {
        throw new Error('No Axios Context');
    }

    return context.instance;
};

export const useIsSessionValid = () => {
    const context = useContext(AxiosStateContext);
    if (!context) {
        throw new Error('No Axios Context');
    }

    return context.sessionValid;
};

export const useResetSession = () => {
    const context = useContext(AxiosStateContext);
    if (!context) {
        throw new Error('No Axios Context');
    }

    return context.resetSession;
};

export const useAxiosAbortController = () => {
    return useCallback(() => {
        abortController.abort();
        abortController = new AbortController();
    }, []);
};

/**
 * Axios instance for use with dlrSecured calls
 */
export const useDSIMAxiosInstance = () => {
    const context = useContext(AxiosStateContext);
    if (!context || !context.dSIMInstance) {
        throw new Error('No Axios Context');
    }

    return context.dSIMInstance;
};

/**
 * Axios instance for use to obtain JWT from GoMotoCore
 */
export const useAxiosAuthInstance = () => {
    const context = useContext(AxiosStateContext);
    if (!context || !context.authInstance) {
        throw new Error('No Axios Context');
    }

    return context.authInstance;
};

export const useAPICall = () => {
    const instance = useAxiosInstance();
    const Localization = useLocalization();

    return useCallback(<T,>(
        /**
         * Callback when the API call succeeds and returns data
         */
        onSuccess?: (data: T) => void,
        /**
         * Callback when the API call is ongoing or complete
         */
        onLoading?: (loading: boolean) => void,
        /**
         * Callback when the API call errors out
         */
        onError?: (error: string) => void,
        /**
         * Callback when the API call has finished, either successfully or from an error
         */
        onComplete?: () => void,
        /**
         * UNCOMMON use. Callback when an error is the result of the user's login token expiring.
         *
         * NOTE: The user is automatically logged out in this scenario. This callback is for special
         * situations where we want to detect these errors (IE The Login component during page refreshes)
         */
        onSessionExpiredError?: (message: string) => void,
        /**
         * UNCOMMON use. Callback when an error is the result of the Axios call being canceled (IE using the AbortController)
         *
         * NOTE: All in progress calls are canceled when the user is logged out. This callback is for special
         * situations where we want to detect these errors.
         */
        onCanceledError?: () => void,
    ) => {
        const LoadingHandler = () => {
            onLoading && onLoading(true);
        };

        const SuccessHandler = (response: AxiosResponse<T>) => {
            onSuccess && onSuccess(response.data);
        };

        const ErrorHandler = (error: AxiosError<APIError>) => {
            let canceled: boolean = error && error.code === 'ERR_CANCELED';
            let expired: boolean = error && error.code === 'SESSION_EXPIRED';

            if (canceled) {
                onCanceledError && onCanceledError();
            }
            else if (expired) {
                onSessionExpiredError && onSessionExpiredError(Localization.SessionExpiredError);
            }
            else {
                onError && onError(
                    error?.response?.data?.message ??
                    error?.message ??
                    Localization.UnexpectedErrorOccurred,
                );
            }
        };

        const CompleteHandler = () => {
            onLoading && onLoading(false);
            onComplete && onComplete();
        };

        return {
            get: (url: string, options: AxiosRequestConfig | undefined = undefined) => {
                LoadingHandler();
                instance.get<T>(url, options)
                    .then(SuccessHandler)
                    .catch(ErrorHandler)
                    .finally(CompleteHandler);
            },
            put: (url: string, data?: any) => {
                LoadingHandler();
                instance.put<T>(url, data)
                    .then(SuccessHandler)
                    .catch(ErrorHandler)
                    .finally(CompleteHandler);
            },
            post: (url: string, data?: any) => {
                LoadingHandler();
                instance.post<T>(url, data)
                    .then(SuccessHandler)
                    .catch(ErrorHandler)
                    .finally(CompleteHandler);
            },
            delete: (url: string, data?: any) => {
                LoadingHandler();
                instance.delete<T>(url, data)
                    .then(SuccessHandler)
                    .catch(ErrorHandler)
                    .finally(CompleteHandler);
            },
        };
    }, [instance, Localization.UnexpectedErrorOccurred, Localization.SessionExpiredError]);
};